import React from "react";
import { Link } from "react-router-dom";
import { GroundTourType, TOUR_TYPE_LABEL } from "../../../../utils/groundListingParams";
import { CARD_TAG_CHIP_LIMIT } from "../../../../utils/tagDisplay";

const ICONS = "/iceholidays-assets/images/icons/ground";

// Not every ground tour has artwork uploaded, and an ActiveStorage variant can
// 404 on a blob that has gone missing, so both cases fall back to the generic
// listing photo rather than leaving an empty media box.
const FALLBACK_IMAGE = "/iceholidays-assets/images/gd-listing-page.png";

// Tour tier, from the land_tours.brand enum. The design ships one badge
// component per tier: a premium palette for 5A/5A+ and a neutral one for 3A/4A,
// each with its own crown artwork. Tours with no brand set show no badge.
const TIER: Record<string, { label: string; modifier: string; tone: "5a" | "3a" }> = {
  gt_3a: { label: "3A", modifier: "3a", tone: "3a" },
  gt_4a: { label: "4A", modifier: "4a", tone: "3a" },
  gt_5a: { label: "5A", modifier: "5a", tone: "5a" },
  gt_5a_plus: { label: "5A+", modifier: "5a-plus", tone: "5a" },
};

export interface GroundTourCardProps {
  image?: string;
  /** Promotion ribbon label, from the tour's primary tag. */
  promotion?: string;
  // Tags after the ribbon: the first two show as chips, the rest collapse into "+N More".
  tags?: string[];
  tourType: GroundTourType;
  /** Day 1 itinerary city. */
  tourStart?: string;
  title: string;
  /** land_tours.brand enum value. */
  brand?: string;
  flightsIncluded?: boolean;
  originalPrice?: string;
  save?: string;
  /** Absent when the tour has no pricing tiers set; the API sorts those last. */
  price?: string;
  minPax: number;
  href?: string;
  /** "compact" gives the card the fixed width/height used in a horizontal scroll carousel. */
  variant?: "grid" | "compact";
}

export default function GroundTourCard({
  image,
  promotion,
  tags = [],
  tourType,
  tourStart,
  title,
  brand,
  flightsIncluded,
  originalPrice,
  save,
  price,
  minPax,
  href,
  variant = "grid",
}: GroundTourCardProps) {
  const tier = brand ? TIER[brand] : undefined;
  const chips = tags.slice(0, CARD_TAG_CHIP_LIMIT);
  const moreCount = tags.length - chips.length;

  const content = (
    <>
      <div className="v2-offer-card__media">
        <img
          src={image || FALLBACK_IMAGE}
          alt={title}
          onError={(event) => {
            const img = event.currentTarget;
            if (!img.src.endsWith(FALLBACK_IMAGE)) img.src = FALLBACK_IMAGE;
          }}
        />
        {promotion && <span className="v2-offer-card__badge">{promotion}</span>}
        <span className={`v2-ground-card__type v2-ground-card__type--${tourType}`}>
          <img src={`${ICONS}/${tourType}-tour-icon.png`} alt="" aria-hidden="true" />
          {TOUR_TYPE_LABEL[tourType]}
        </span>
      </div>

      <div className="v2-offer-card__body">
        <div className="v2-offer-card__content">
          {tourStart && (
            <div className="v2-ground-card__start">
              <img src={`${ICONS}/tour-start.svg`} alt="" aria-hidden="true" />
              Tour start - {tourStart}
            </div>
          )}

          <h3 className="v2-offer-card__title">{title}</h3>

          {(tier || flightsIncluded) && (
            <div className="v2-offer-card__tag-row">
              {tier && (
                <span className={`v2-ground-card__tier v2-ground-card__tier--${tier.modifier}`}>
                  <img src={`${ICONS}/tier-${tier.tone}.svg`} alt="" aria-hidden="true" />
                  {tier.label}
                </span>
              )}
              {flightsIncluded && <span className="v2-offer-card__tag">Flights Included</span>}
            </div>
          )}

          {chips.length > 0 && (
            <div className="v2-offer-card__promo-tags">
              {chips.map((tag) => (
                <span className="v2-offer-card__promo-tag" key={tag}>{tag}</span>
              ))}
              {moreCount > 0 && <span className="v2-offer-card__promo-tag">+{moreCount} More</span>}
            </div>
          )}
        </div>

        {originalPrice && (
          <div className="v2-offer-card__pricing">
            <span className="v2-offer-card__from">
              From <span className="v2-offer-card__orig">{originalPrice}</span>
            </span>
            {save && <span className="v2-offer-card__save-tag">Save {save}</span>}
          </div>
        )}

        {price && (
          <div className="v2-offer-card__price">
            {price} <span>/ person</span>
          </div>
        )}

        <div className="v2-offer-card__footnote">Min. {minPax} Pax</div>
      </div>
    </>
  );

  const className = `v2-offer-card v2-offer-card--ground${variant === "compact" ? " v2-offer-card--compact" : ""}`;

  // The ground tour detail page is a later phase, so until it exists the card
  // renders as a plain tile rather than linking to a route that 404s.
  return href ? (
    <Link to={href} className={className}>
      {content}
    </Link>
  ) : (
    <div className={className}>{content}</div>
  );
}
